import { useEffect, useMemo, useState } from "react";
import { Api } from "../../api/api";
import { MasterPullRequest, PullRequestBranch } from "./PullRequests.types";

const matchesQuery = (pullRequest: MasterPullRequest, query: string) => {
  if (!query) {
    return true;
  }

  const softConfig = pullRequest.workItems.some((workItem) => workItem.softConfigRequired)
    ? "yes soft config required"
    : "no soft config";

  return [
    pullRequest.title,
    pullRequest.author,
    pullRequest.status,
    String(pullRequest.id),
    softConfig,
    ...pullRequest.workItems.flatMap((workItem) => [
      workItem.title,
      workItem.type,
      workItem.state,
      String(workItem.id),
      workItem.softConfigRequired ? "yes soft config required" : "no soft config",
    ]),
  ]
    .join(" ")
    .toLowerCase()
    .includes(query);
};

export const usePullRequests = (branch: PullRequestBranch) => {
  const [pullRequests, setPullRequests] = useState<MasterPullRequest[]>([]);
  const [softConfigFieldFound, setSoftConfigFieldFound] = useState(true);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await Api.getBranchPullRequests(branch);
        if (!cancelled) {
          setPullRequests(response.data.pullRequests ?? []);
          setSoftConfigFieldFound(response.data.softConfigFieldFound);
        }
      } catch {
        if (!cancelled) {
          setError("Could not load pull requests from Azure DevOps.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [branch]);

  const visiblePullRequests = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return pullRequests
      .filter((pullRequest) => matchesQuery(pullRequest, normalizedQuery))
      .sort((first, second) => {
        const firstDate = new Date(first.closedDate || first.creationDate).getTime();
        const secondDate = new Date(second.closedDate || second.creationDate).getTime();
        return secondDate - firstDate;
      });
  }, [pullRequests, query]);

  return {
    pullRequests: visiblePullRequests,
    query,
    setQuery,
    isLoading,
    error,
    softConfigFieldFound,
    total: pullRequests.length,
    visible: visiblePullRequests.length,
  };
};
